import React, { useEffect, useRef, useState } from 'react';
import { API } from '../services/api';
import './DailyTrends.css';

function DailyTrends({ store: propStore }) {
  const scrollContainerRef = useRef(null);
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState(propStore || '');
  const [stores, setStores] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Load stores list
  useEffect(() => {
    API.getStores()
      .then(storeList => {
        setStores(storeList);
        if (storeList.length > 0) {
          setSelectedStore(currentStore => currentStore || storeList[0]);
        }
      })
      .catch(err => console.error('Error loading stores:', err));
  }, []);

  // Set default date to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
  }, []);

  // Load time window data
  useEffect(() => {
    if (!selectedStore || !selectedDate) return;
    
    setLoading(true);
    API.getTimeWindows(1, selectedStore, selectedDate)
      .then(timeWindowData => {
        // Transform data into table format
        // Group by item_name and time_window_id
        const itemMap = {};
        
        timeWindowData.forEach(record => {
          const itemName = record.item_name || record.item_id;
          const timeWindow = record.time_window_id;
          
          if (!itemMap[itemName]) {
            itemMap[itemName] = {};
          }
          
          // Store the status for this time window (OUT_OF_STOCK takes precedence if multiple)
          if (!itemMap[itemName][timeWindow] || record.status === 'OUT_OF_STOCK') {
            itemMap[itemName][timeWindow] = record.status;
          }
        });
        
        // Convert to array format for table
        const tableRows = Object.keys(itemMap).map(itemName => ({
          itemName,
          timeWindows: itemMap[itemName]
        }));
        
        setTableData(tableRows);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading time window data:', err);
        setLoading(false);
      });
  }, [selectedStore, selectedDate]);

  // Time window labels (1-20 = 30-minute intervals from 11am-9pm)
  const timeWindowLabels = {
    '1': '11am-11:30am',
    '2': '11:30am-12pm',
    '3': '12pm-12:30pm',
    '4': '12:30pm-1pm',
    '5': '1pm-1:30pm',
    '6': '1:30pm-2pm',
    '7': '2pm-2:30pm',
    '8': '2:30pm-3pm',
    '9': '3pm-3:30pm',
    '10': '3:30pm-4pm',
    '11': '4pm-4:30pm',
    '12': '4:30pm-5pm',
    '13': '5pm-5:30pm',
    '14': '5:30pm-6pm',
    '15': '6pm-6:30pm',
    '16': '6:30pm-7pm',
    '17': '7pm-7:30pm',
    '18': '7:30pm-8pm',
    '19': '8pm-8:30pm',
    '20': '8:30pm-9pm',
  };
  
  const timeWindowIds = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20'];

  const handleMouseDown = (event) => {
    if (event.button !== 0 || !scrollContainerRef.current) return;

    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: scrollContainerRef.current.scrollLeft,
      scrollTop: scrollContainerRef.current.scrollTop,
    };
    setIsDragging(true);
  };

  const handleMouseMove = (event) => {
    const { isDragging: dragging, startX, startY, scrollLeft, scrollTop } = dragStateRef.current;
    if (!dragging || !scrollContainerRef.current) return;

    event.preventDefault();
    scrollContainerRef.current.scrollLeft = scrollLeft - (event.clientX - startX);
    scrollContainerRef.current.scrollTop = scrollTop - (event.clientY - startY);
  };

  const stopDragging = () => {
    dragStateRef.current.isDragging = false;
    setIsDragging(false);
  };

  if (loading) return <div className="component-loading">Loading time window data...</div>;

  return (
    <div className="daily-trends">
      <div className="trends-header">
        <h2>Hourly Inventory Stock Status</h2>
        <div className="trends-controls">
          <label>
            Store:
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="store-selector"
            >
              <option value="">All Stores</option>
              {stores.map(store => (
                <option key={store} value={store}>{store}</option>
              ))}
            </select>
          </label>
          <label>
            Date:
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-selector"
            />
          </label>
        </div>
      </div>

      {tableData.length > 0 ? (
        <div
          ref={scrollContainerRef}
          className={`time-window-table-container ${isDragging ? 'is-dragging' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDragging}
          onMouseLeave={stopDragging}
        >
          <div className="time-window-table-scroll">
            <table className="time-window-table">
              <thead>
                <tr>
                  <th className="item-name-col">Item</th>
                  {timeWindowIds.map(twId => (
                    <th key={twId} className="time-window-col">
                      {timeWindowLabels[twId]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, idx) => (
                  <tr key={idx}>
                    <td className="item-name-cell">{row.itemName}</td>
                    {timeWindowIds.map(twId => {
                      const status = row.timeWindows[twId];
                      const cellClass = status === 'OUT_OF_STOCK' 
                        ? 'status-out-of-stock' 
                        : status === 'IN_STOCK' 
                          ? 'status-in-stock' 
                          : 'status-unknown';
                      return (
                        <td key={twId} className={`time-window-cell ${cellClass}`}>
                          {status ? (status === 'OUT_OF_STOCK' ? '●' : '●') : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table-legend">
            <span className="legend-item">
              <span className="legend-color status-in-stock">●</span> In Stock
            </span>
            <span className="legend-item">
              <span className="legend-color status-out-of-stock">●</span> Out of Stock
            </span>
            <span className="legend-item">
              <span className="legend-color status-unknown"></span> No Data
            </span>
          </div>
        </div>
      ) : (
        <div className="no-data">No data available for the selected store and date</div>
      )}
    </div>
  );
}

export default DailyTrends;

